import { Link } from 'react-router-dom';

import classNames from 'classnames';

import styles from './_index.module.scss';
import type { IconCTAContractValues, IconCTAProps } from './types';

export type { IconCTAProps } from './types';

const warnAboutInvalidContract = ({
  ariaLabel,
  href,
  onClick,
  path,
  text,
}: IconCTAContractValues) => {
  if (!import.meta.env.DEV) return;

  if (!ariaLabel?.trim() && !text?.trim()) {
    console.error('IconCTA requires non-empty visible text or an aria-label.');
  }

  const interactionModeCount = [
    href !== undefined,
    path !== undefined,
    onClick !== undefined,
  ].filter(Boolean).length;

  if (interactionModeCount !== 1) {
    console.error('IconCTA requires exactly one of href, path, or onClick.');
  }
};

const IconCTA = ({
  'aria-label': ariaLabel,
  className,
  'data-testid': dataTestId = 'icon-cta',
  href,
  icon,
  onClick,
  path,
  state,
  text,
  ...props
}: IconCTAProps) => {
  warnAboutInvalidContract({ ariaLabel, href, onClick, path, text });

  let iconCta = null;
  const iconCtaClasses = classNames(styles['icon-cta'], className);

  if (href !== undefined) {
    iconCta = (
      <a
        aria-label={ariaLabel}
        className={iconCtaClasses}
        data-testid={dataTestId}
        href={href}
        {...props}
      >
        <span aria-hidden="true">{icon}</span>
        {text && <span>{text}</span>}
      </a>
    );
  } else if (path !== undefined) {
    iconCta = (
      <Link
        aria-label={ariaLabel}
        className={iconCtaClasses}
        data-testid={dataTestId}
        state={state}
        to={path}
        {...props}
      >
        <span aria-hidden="true">{icon}</span>
        {text && <span>{text}</span>}
      </Link>
    );
  } else if (onClick) {
    iconCta = (
      <button
        aria-label={ariaLabel}
        className={iconCtaClasses}
        data-testid={dataTestId}
        onClick={onClick}
        type="button"
        {...props}
      >
        <span aria-hidden="true">{icon}</span>
        {text && <span>{text}</span>}
      </button>
    );
  }

  return iconCta;
};

export default IconCTA;
